import { useState } from 'react';
import {
  Button,
  ButtonGroup,
  InlineStack,
  Panel,
  PanelSection,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { canSaveMParticleSettings, type MParticleIntegration } from '@/services/db/mparticle-integration/entity';
import {
  useConnectMParticleIntegration,
  useDisconnectMParticleIntegration,
  useSaveMParticleIntegration,
} from '@/services/db/mparticle-integration/mutations';
import { ConnectForm } from './connect-form';
import { SettingsForm, type MParticleSettings } from './settings-form';
import { useIntegrationsOverviewHref } from '../hooks';

type MParticleConnectorProps = {
  /** The saved record, or `null` when the workspace has never connected. */
  integration: MParticleIntegration | null;
};

function settingsOf(integration: MParticleIntegration | null): MParticleSettings {
  return {
    enabled: integration?.enabled ?? false,
    events: integration?.events ?? [],
    resolution: integration?.resolution ?? false,
  };
}

function isSameSettings(a: MParticleSettings, b: MParticleSettings): boolean {
  return (
    a.enabled === b.enabled &&
    a.resolution === b.resolution &&
    a.events.length === b.events.length &&
    a.events.every((event) => b.events.includes(event))
  );
}

/**
 * The connector's panel and its footer actions.
 *
 * The page remounts this on connect or disconnect, so the draft below is seeded
 * once per record rather than synchronised to it.
 */
export function MParticleConnector({ integration }: MParticleConnectorProps) {
  const workspace = useCurrentWorkspace();
  const { hasPermission } = usePermissions();
  const snackbars = useSnackbars();
  const safeNavigate = useSafeNavigate();
  const overviewHref = useIntegrationsOverviewHref(String(workspace?.id ?? ''));

  const isReadOnly = !hasPermission('journeys/mParticle/edit');
  // Removing is its own permission. Ember gated every control on `edit`, including
  // this one, so a role granted edit-without-delete could still disconnect.
  const canRemove = hasPermission('journeys/mParticle/delete');
  const isConnected = integration != null;

  const saved = settingsOf(integration);
  const [draft, setDraft] = useState<MParticleSettings>(saved);

  const connect = useConnectMParticleIntegration({
    mutationConfig: {
      onError: () =>
        snackbars.create({
          variant: 'critical',
          id: 'mparticle-connect.error',
          message: 'Could not connect mParticle. Please check your credentials and try again.',
        }),
    },
  });

  const save = useSaveMParticleIntegration({
    mutationConfig: {
      onSuccess: (updated, submitted) => {
        // Adopt the server's copy only when the form still holds what was sent.
        // The settings stay editable during a save, so a selection made after
        // submitting must survive: it stays dirty and the Save button offers it
        // again, rather than being silently replaced by the older response.
        setDraft((current) => (isSameSettings(current, submitted) ? settingsOf(updated) : current));
        snackbars.create({
          variant: 'default',
          id: 'mparticle.success',
          message: 'mParticle integration settings saved',
        });
      },
      // Ember rolled the record back here, discarding the edit. Keeping it lets a
      // retry work instead of making the user redo the selection after a 500 —
      // an intentional deviation, and the cache is untouched either way.
      onError: () =>
        snackbars.create({
          variant: 'critical',
          id: 'mparticle-save.error',
          message: 'Could not update mParticle integration. Try again.',
        }),
    },
  });

  const disconnect = useDisconnectMParticleIntegration({
    mutationConfig: {
      onSuccess: () =>
        snackbars.create({
          variant: 'default',
          id: 'mparticle-delete.success',
          message: 'mParticle integration removed',
        }),
      onError: () =>
        snackbars.create({
          variant: 'critical',
          id: 'mparticle-delete.error',
          message: 'Could not remove mParticle integration. Try again.',
        }),
    },
  });

  // Ember's `canSaveIntegration`: dirty, and at least one event selected.
  const isDirty = !isSameSettings(draft, saved);
  const canSave = isDirty && canSaveMParticleSettings(draft.events) && !isReadOnly;

  return (
    <>
      <Panel withSectionBorders>
        <PanelSection>
          <ConnectForm
            serverKey={integration?.key ?? ''}
            isConnected={isConnected}
            isReadOnly={isReadOnly}
            canRemove={canRemove}
            isConnecting={connect.isPending}
            isDisconnecting={disconnect.isPending}
            isSaving={save.isPending}
            onConnect={async (credentials) => {
              // The error surfaces as a snackbar from the mutation config; swallow the rejection so
              // Pluma's form unlocks instead of leaving the fields stuck behind a pending promise.
              await connect.mutateAsync(credentials).catch(() => undefined);
            }}
            onDisconnect={() => {
              if (integration != null) disconnect.mutate({ integrationId: integration.id });
            }}
          />
        </PanelSection>

        {integration == null ? null : <SettingsForm settings={draft} isReadOnly={isReadOnly} onChange={setDraft} />}
      </Panel>

      {integration == null ? null : (
        <InlineStack alignInline="end" alignBlock="center" mt="200">
          <ButtonGroup groupVariant="spaced">
            <Button variant="secondary" onClick={() => safeNavigate(overviewHref)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={!canSave || disconnect.isPending}
              isLoading={save.isPending}
              onClick={() => save.mutate({ integrationId: integration.id, ...draft })}
            >
              Save integration
            </Button>
          </ButtonGroup>
        </InlineStack>
      )}
    </>
  );
}
